import classnames from 'classnames';
import { isEqual, isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { capitaliseStartLetter } from '../../../../utils/regex-utils';
import { getIsEditingSelectedSubCounterparty, getSubCounterpartyScopeFilters, setSelectedOpinionSubCounterparty, SubCounterpartyType } from '../../../admin/sub-counterparty-types/store';
import { LongText } from '../../../shared/icons';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Toggle } from '../../../shared/toggle';
import { IconTooltip, InformationTooltip, OverflowTooltip } from '../../../shared/tooltip';
import { CounterpartyCoverage, updateIncludedInstanceSubCounterpartyTypes } from '../store';
import styles from './OpinionSection.module.scss';
import { OpinionScope } from '../../../admin/opinions/store';
import { scopeFormatter } from '../../../shared/table/arkTableFormatters';

interface SubCounterpartyColumnProps {
    columnId: string;
    readOnly: boolean;
    subCounterpartyTypes: SubCounterpartyType[];
    updateIncludedIds: (id: number) => void;
    isEditingSubCounterparty: boolean;
    setSelectedSubCounterparty: (subCounterparty: SubCounterpartyType) => void;
    label?: string;
    scope: OpinionScope | null;
}

export const SubCounterpartyColumn: React.FC<SubCounterpartyColumnProps> = ({
    columnId,
    subCounterpartyTypes,
    readOnly,
    updateIncludedIds,
    isEditingSubCounterparty,
    label = capitaliseStartLetter(columnId),
    setSelectedSubCounterparty,
    scope
}) => {
    const isIncludedColumn = useMemo(() => columnId === 'included', [columnId]);
    const includedTooltipContent = useMemo(() => `Based on the provided opinion, these sub counterparty types are ${isIncludedColumn ? 'included in' : 'excluded from'} the netting analysis`, [isIncludedColumn]);
    const toggleIncluded = useCallback((id: number, e: MouseEvent | React.SyntheticEvent<MouseEvent | KeyboardEvent, Event>) => {
        e.stopPropagation();
        updateIncludedIds(id);
    }, [updateIncludedIds]);

    const getIsOutOfScope = useCallback((scopes: OpinionScope[]) => !isNull(scope) && !scopes.includes(scope), [scope]);
    const getOutOfScopeTooltipContent = useCallback((scopes: OpinionScope[]) => `Scope: ${scopes.map(scope => scopeFormatter(scope)).join(', ')}`, []);

    return (
        <div className={classnames(styles.subCounterpartyTypesColumnListWrapper, {
            [styles.included]: isIncludedColumn,
            [styles.excluded]: !isIncludedColumn
        })}>
            <div className={styles.subCounterpartyTypesColumnHeader}>
                <OverflowTooltip className={styles.subCounterpartyTypesColumnTitle} overlayText={label} />
                <InformationTooltip content={includedTooltipContent} />
            </div>
            <Scrollable height={'calc(100% - 25px'}>
                {subCounterpartyTypes.map((subCounterparty, index) => {
                    const { subCounterpartyTypeId, subCounterpartyName, subCounterpartyDescription, scopes } = subCounterparty;
                    const isOutOfScope = getIsOutOfScope(scopes);
                    const outOfScopeTooltip = getOutOfScopeTooltipContent(scopes);
                    return (
                        <div className={classnames(styles.subCounterpartyConfigToggleCard, { [styles.outOfScope]: isOutOfScope })} key={index} onDoubleClick={() => setSelectedSubCounterparty(subCounterparty)}>
                            <div className={styles.subCounterparty} style={{ width: `calc(100% - ${readOnly ? 0 : 46}px)`, marginRight: `${readOnly ? 0 : 10}px` }}>
                                <OverflowTooltip className={styles.subCounterpartyName} overlayText={subCounterpartyName} />
                                {!isNull(subCounterpartyDescription) && <div><IconTooltip content={subCounterpartyDescription} icon={LongText} fontSize={25} width={500} /></div>}
                            </div>
                            {!readOnly && !isOutOfScope && <Toggle checked={isIncludedColumn} onChange={(val, e) => toggleIncluded(subCounterpartyTypeId!, e)} disabled={isEditingSubCounterparty} />}
                            {isOutOfScope && <InformationTooltip content={outOfScopeTooltip} />}
                        </div>
                    );
                })}
            </Scrollable>
        </div>
    );
};

interface SubCounterpartyConfigColumnsProps {
    counterpartyCoverage: CounterpartyCoverage;
    readOnly: boolean;
    subCounterpartyTypes: SubCounterpartyType[];
    outOfScopeSubCounterpartyTypes: SubCounterpartyType[];
    height: string;
    selectedParentCounterpartyType: string;
    userHasIndustryStandardPermissions: boolean;
    scope: OpinionScope | null;
}

export const SubCounterpartyConfigColumns: React.FC<SubCounterpartyConfigColumnsProps> = ({ readOnly, counterpartyCoverage, subCounterpartyTypes, height, selectedParentCounterpartyType, outOfScopeSubCounterpartyTypes, userHasIndustryStandardPermissions, scope }) => {
    const dispatch = useAppDispatch();
    const isEditingSubCounterparty = useAppSelector(getIsEditingSelectedSubCounterparty);
    const scopeFilters = useAppSelector(getSubCounterpartyScopeFilters);

    const shouldIncludeOutOfScope = useMemo(() => isEqual(scopeFilters, Object.values(OpinionScope)), [scopeFilters]);

    const includedIds = useMemo(() => counterpartyCoverage.standardOptions.find(({ label }) => label === selectedParentCounterpartyType)?.includedSubCounterpartyTypeIds || [], [counterpartyCoverage, selectedParentCounterpartyType]);

    const includedSubCounterpartyTypes = useMemo(() => subCounterpartyTypes.filter(({ subCounterpartyTypeId }) => includedIds.includes(subCounterpartyTypeId!)), [includedIds, subCounterpartyTypes]);
    const inScopeExcludedSubCounterpartyTypes = useMemo(() => subCounterpartyTypes.filter(({ subCounterpartyTypeId }) => !includedIds.includes(subCounterpartyTypeId!)), [includedIds, subCounterpartyTypes]);
    const excludedSubCounterpartyTypes = useMemo(() => shouldIncludeOutOfScope ? [...inScopeExcludedSubCounterpartyTypes, ...outOfScopeSubCounterpartyTypes] : inScopeExcludedSubCounterpartyTypes, [inScopeExcludedSubCounterpartyTypes, outOfScopeSubCounterpartyTypes, shouldIncludeOutOfScope]);

    const updateIncludedIds = useCallback((subCounterpartyTypeId: number) => {
        const newIncludedArray = includedIds.includes(subCounterpartyTypeId)
            ? includedIds.filter(id => id !== subCounterpartyTypeId)
            : [...includedIds, subCounterpartyTypeId];

        dispatch(updateIncludedInstanceSubCounterpartyTypes(selectedParentCounterpartyType, newIncludedArray));
    }, [dispatch, includedIds, selectedParentCounterpartyType]);

    const setSelectedSubCounterparty = useCallback((subCounterparty: SubCounterpartyType) => {
        if ((subCounterparty.isSystem === 0 || userHasIndustryStandardPermissions) && !readOnly) {
            dispatch(setSelectedOpinionSubCounterparty(subCounterparty));
        }
    }, [dispatch, userHasIndustryStandardPermissions, readOnly]);

    return (
        <div className={styles.subCounterpartyConfigCardsContentWrapper} style={{ height: height }}>
            <SubCounterpartyColumn
                columnId='included'
                subCounterpartyTypes={includedSubCounterpartyTypes}
                readOnly={readOnly}
                updateIncludedIds={updateIncludedIds}
                isEditingSubCounterparty={isEditingSubCounterparty}
                setSelectedSubCounterparty={setSelectedSubCounterparty}
                scope={scope}
            />
            <SubCounterpartyColumn
                columnId='excluded'
                subCounterpartyTypes={excludedSubCounterpartyTypes}
                readOnly={readOnly}
                updateIncludedIds={updateIncludedIds}
                isEditingSubCounterparty={isEditingSubCounterparty}
                setSelectedSubCounterparty={setSelectedSubCounterparty}
                scope={scope}
            />
        </div>
    );
};
